import { useState } from 'react';
import clsx from 'clsx';

import { MaybeMatch } from '@nf/utils-common/fpw/monadic';

export interface UseAccordionProps {
	as?: React.ElementType;
	className?: string;
	allowMultiple?: boolean;
	expandedItemKeys?: string[];
	onSelectionChange?(eventKeys: string[]): void;
}

const isExpandedItem = (expandedItemKeys: string[], itemKey: string): boolean => 
	expandedItemKeys.includes(itemKey);

const toggleExpandedItem = (expandedItemKeys: string[], itemKey: string): string[] => {
	return isExpandedItem(expandedItemKeys, itemKey)
		? expandedItemKeys.filter(key => key !== itemKey)
		: [...expandedItemKeys, itemKey];
};

export const useAccordion = (props: UseAccordionProps) => {
	const {
		as,
		className,
		allowMultiple = false,
		expandedItemKeys = [],
		onSelectionChange,
		...other
	} = props;
	const [activeItemKeys, setActiveItemKeys] = useState(expandedItemKeys);

	const Component = as || 'div';
	const classNames = clsx('accordion', className);

	const changeSelection = (itemKey: string) => {
		const itemKeys: string[] = 
			MaybeMatch(allowMultiple).fold(
				() => isExpandedItem(activeItemKeys, itemKey) ? []: [itemKey],
				() => toggleExpandedItem(activeItemKeys, itemKey)
			);
		setActiveItemKeys(itemKeys);
		onSelectionChange?.(itemKeys);
	};

	const isAccordionItemExpanded = (itemKey: string) => isExpandedItem(activeItemKeys, itemKey);

	return {
		Component,
		classNames,
		allowMultiple,
		expandedItemKeys: activeItemKeys,
		changeSelection,
		isAccordionItemExpanded,
		...other
	};
};